import { CheckIcon, HourglassMediumIcon, ProhibitIcon, ThumbsUpIcon, TruckIcon } from '@phosphor-icons/react'

import {
  normalizedTrackingStatus,
  ShipmentOutForDeliveryIcon,
  shipmentStatuses,
  shipmentStatusLabels,
  theme,
} from '@reveni-io/reveni-ui'

import ParcelPackedIcon from './components/ParcelPackedIcon/ParcelPackedIcon'

export const parcelDirections = {
  outbound: 'outbound',
  inbound: 'inbound',
}

export const parcelTypes = {
  outbound: 'outbound',
  inbound: 'inbound',
  tracking: 'tracking',
}

export const outboundExchangeKey = 'outboundExchange'

export const lineItemStatuses = {
  packed: 'packed',
  discarded: 'discarded',
  pending: 'pending',
}

export const parcelTypesByDirection = {
  [parcelDirections.outbound]: [parcelTypes.outbound, parcelTypes.tracking],
  [parcelDirections.inbound]: [parcelTypes.inbound],
}

export const parcelStatusInfo = {
  [shipmentStatuses.requested]: {
    label: shipmentStatusLabels.requested,
    Icon: HourglassMediumIcon,
    backgroundColor: theme.legacy.palette.pink.tone300,
    color: theme.legacy.colors.text.default,
  },
  [shipmentStatuses.packed]: {
    label: shipmentStatusLabels.packed,
    Icon: ParcelPackedIcon,
    backgroundColor: theme.legacy.palette.pink.tone500,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
  [shipmentStatuses.ready]: {
    label: shipmentStatusLabels.ready,
    Icon: ThumbsUpIcon,
    backgroundColor: theme.legacy.palette.lilac.tone500,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
  [shipmentStatuses.shipped]: {
    label: shipmentStatusLabels.shipped,
    Icon: TruckIcon,
    backgroundColor: theme.legacy.palette.blue.tone500,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
  [shipmentStatuses.out_for_delivery]: {
    label: shipmentStatusLabels.out_for_delivery,
    Icon: ShipmentOutForDeliveryIcon,
    backgroundColor: theme.legacy.palette.blue.tone700,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
  [shipmentStatuses.delivered]: {
    label: shipmentStatusLabels.delivered,
    Icon: CheckIcon,
    backgroundColor: theme.legacy.palette.teal.tone500,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
  [shipmentStatuses.cancelled]: {
    label: shipmentStatusLabels.cancelled,
    Icon: ProhibitIcon,
    backgroundColor: theme.legacy.palette.grey.tone900,
    color: theme.legacy.palette.whiteAlpha.alpha100,
  },
}

export const parcelStatusesByDirection = {
  [parcelDirections.outbound]: [
    shipmentStatuses.requested,
    shipmentStatuses.packed,
    shipmentStatuses.ready,
    shipmentStatuses.shipped,
    shipmentStatuses.out_for_delivery,
    shipmentStatuses.delivered,
  ],
  [parcelDirections.inbound]: [
    shipmentStatuses.requested,
    shipmentStatuses.ready,
    shipmentStatuses.shipped,
    shipmentStatuses.out_for_delivery,
    shipmentStatuses.delivered,
  ],
}

export const alertTypes = {
  wrongAddress: 'wrong_address',
  incompleteAddress: 'incomplete_address',
  labelCreationError: 'label_creation_error',
  pickupCreationError: 'pickup_creation_error',
  pickupFailed: 'pickup_failed',
  rejectedByRecipient: 'rejected_by_recipient',
  returnedToSender: 'returned_to_sender',
  unableToDeliver: 'unable_to_deliver',
  recipientMissing: 'recipient_missing',
  deliveryPostponed: 'delivery_postponed',
  shipmentCancellationFailed: 'shipment_cancellation_failed',
  unknown: 'unknown',
  missingCustomsData: 'missing_customs_data',
  packageDestroyed: 'package_destroyed',
  packageLost: 'package_lost',
  packageHeld: 'package_held',
  packagedNotDelivered: 'package_not_delivered',
  statusStalled: 'status_stalled',
  orderNotFound: 'order_not_found',
  orderInvalid: 'order_invalid',
  orderAnnouncedNotCollected: 'order_announced_not_collected',
  labelGenerationFailed: 'label_generation_failed',
  shipmentReturnCollectFail: 'shipment_return_collect_fail',
  returnLabelCreated: 'return_label_created',
  customsPaymentRequired: 'customs_payment_required',
  customsAdditionalInfoRequired: 'customs_additional_info_required',
  customsProhibitedContent: 'customs_prohibited_content',
  customsInspection: 'customs_inspection',
  shipmentInTransitDeliveryDelayed: 'shipment_in_transit_delivery_delayed',
  customsRetainedQuarantine: 'customs_retained_quarantine',
  trackingExpired: 'tracking_expired',
  customsHeldAdditionalInfoRequired: 'customs_held_additional_info_required',
  deliveryAttemptFailedRecipientNotAtHome: 'delivery_attempt_failed_recipient_not_home',
  deliveryAttemptFailedNoticeLeft: 'delivery_attempt_failed_notice_left',
  deliveryAttemptFailedAccessIssue: 'delivery_attempt_failed_access_issue',
  deliveryAttemptFailedGeneralIssue: 'delivery_attempt_failed_general_issue',
  deliveryAttemptFailedAddressInvalid: 'delivery_attempt_failed_address_invalid',
  deliveryAttemptCollectionPointExceeded: 'delivery_attempt_collection_point_exceeded',
  exceptionAlert: 'exception_alert',
  shipmentReturnParcelLost: 'shipment_return_parcel_lost',
  shipmentReturnParcelDeclaredLost: 'shipment_return_parcel_declared_lost',
  shipmentReturnParcelDestroyedDamaged: 'shipment_return_parcel_destroyed_damaged',
  shipmentReturnParcelDestroyedProhibited: 'shipment_return_parcel_destroyed_prohibited',
}

export const alertLabels = {
  [alertTypes.wrongAddress]: 'Dirección incorrecta',
  [alertTypes.incompleteAddress]: 'Dirección incompleta',
  [alertTypes.labelCreationError]: 'Error al solicitar la etiqueta de envío',
  [alertTypes.pickupCreationError]: 'Recogida fallida',
  [alertTypes.pickupFailed]: 'Recogida fallida por transportista',
  [alertTypes.rejectedByRecipient]: 'Intento de entrega - Rechazado por destinatario',
  [alertTypes.returnedToSender]: 'Devuelto al remitente',
  [alertTypes.unableToDeliver]: 'Intento de entrega - Fallido',
  [alertTypes.recipientMissing]: 'Destinatario ausente',
  [alertTypes.deliveryPostponed]: 'Entrega pospuesta',
  [alertTypes.shipmentCancellationFailed]: 'Error al cancelar el envío',
  [alertTypes.unknown]: 'Estado desconocido – contactar con el transportista',
  [alertTypes.missingCustomsData]: 'Error al generar etiqueta de devolución – falta información de aduanas',
  [alertTypes.packageDestroyed]: 'Paquete destruido',
  [alertTypes.packageLost]: 'Paquete perdido',
  [alertTypes.packageHeld]: 'Paquete retenido',
  [alertTypes.packagedNotDelivered]: 'Paquete no entregado',
  [alertTypes.statusStalled]: 'Envío estancado',
  [alertTypes.orderNotFound]: 'Pedido no encontrado',
  [alertTypes.orderInvalid]: 'Pedido inválido',
  [alertTypes.orderAnnouncedNotCollected]: 'Envío anunciado no recogido',
  [alertTypes.labelGenerationFailed]: 'Error al crear etiqueta',
  [alertTypes.shipmentReturnCollectFail]: 'Incidencia envío/devolución – Error en recogida',
  [alertTypes.returnLabelCreated]: 'Devolución al remitente - Etiqueta de devolución creada',
  [alertTypes.customsPaymentRequired]: 'Aduana - Pago requerido',
  [alertTypes.customsAdditionalInfoRequired]: 'Aduana - Se requiere información adicional',
  [alertTypes.customsProhibitedContent]: 'Aduana - Contenido prohibido',
  [alertTypes.customsInspection]: 'Aduana - Inspección',
  [alertTypes.shipmentInTransitDeliveryDelayed]: 'Envío retrasado',
  [alertTypes.customsRetainedQuarantine]: 'Aduana - Retenido en cuarentena',
  [alertTypes.trackingExpired]: 'Seguimiento expirado',
  [alertTypes.customsHeldAdditionalInfoRequired]: 'Aduana - Retenido: información adicional requerida',
  [alertTypes.deliveryAttemptFailedRecipientNotAtHome]: 'Intento de entrega - Destinatario ausente',
  [alertTypes.deliveryAttemptFailedNoticeLeft]: 'Intento de entrega - Aviso dejado',
  [alertTypes.deliveryAttemptFailedAccessIssue]: 'Intento de entrega - Destino inaccesible',
  [alertTypes.deliveryAttemptFailedGeneralIssue]: 'Intento de entrega – Incidencia general',
  [alertTypes.deliveryAttemptFailedAddressInvalid]: 'Intento de entrega - Dirección no válida',
  [alertTypes.deliveryAttemptCollectionPointExceeded]: 'Intento de entrega - Punto de recogida lleno',
  [alertTypes.exceptionAlert]: 'Incidencia en el envío',
  [alertTypes.shipmentReturnParcelLost]: 'Paquete reportado como perdido',
  [alertTypes.shipmentReturnParcelDeclaredLost]: 'Paquete confirmado como perdido',
  [alertTypes.shipmentReturnParcelDestroyedDamaged]: 'Paquete destruido: daño crítico',
  [alertTypes.shipmentReturnParcelDestroyedProhibited]: 'Paquete destruido: contenido ilegal',
}

export const normalizedTrackingStatusMessages = {
  [shipmentStatuses.requested]: {
    order_announced: normalizedTrackingStatus.order_announced,
  },
  [shipmentStatuses.packed]: {
    label_requested: normalizedTrackingStatus.label_requested,
    shipping_method_modified: normalizedTrackingStatus.shipping_method_modified,
  },
  [shipmentStatuses.ready]: {
    label_created: normalizedTrackingStatus.label_created,
    ready_for_pickup: normalizedTrackingStatus.ready_for_pickup,
  },
  [shipmentStatuses.shipped]: {
    shipment_picked_up_by_carrier: normalizedTrackingStatus.shipment_picked_up_by_carrier,
    parcel_in_transit: normalizedTrackingStatus.parcel_in_transit,
    en_route_to_classification_hub: normalizedTrackingStatus.en_route_to_classification_hub,
    processing_hub_received: normalizedTrackingStatus.processing_hub_received,
    processing_hub_sorted: normalizedTrackingStatus.processing_hub_sorted,
    processing_hub_not_sorted: normalizedTrackingStatus.processing_hub_not_sorted,
    processing_hub_relabeled: normalizedTrackingStatus.processing_hub_relabeled,
    processing_hub_departure: normalizedTrackingStatus.processing_hub_departure,
    pallet_added: normalizedTrackingStatus.pallet_added,
    pallet_extracted_from_pallet: normalizedTrackingStatus.pallet_extracted_from_pallet,
    pallet_remove_from_group: normalizedTrackingStatus.pallet_remove_from_group,
    pallet_pallet_disassembled: normalizedTrackingStatus.pallet_pallet_disassembled,
    pallet_filling_at_wharehouse: normalizedTrackingStatus.pallet_filling_at_wharehouse,
    customs_received: normalizedTrackingStatus.customs_received,
    customs_presented_for_clearance: normalizedTrackingStatus.customs_presented_for_clearance,
    customs_awaiting_clearance: normalizedTrackingStatus.customs_awaiting_clearance,
    customs_cleared: normalizedTrackingStatus.customs_cleared,
    en_route_to_destination_country: normalizedTrackingStatus.en_route_to_destination_country,
    en_route_to_destination_depot: normalizedTrackingStatus.en_route_to_destination_depot,
    en_route_to_final_hub: normalizedTrackingStatus.en_route_to_final_hub,
    en_route: normalizedTrackingStatus.en_route,
    order_released: normalizedTrackingStatus.order_released,
  },
  [shipmentStatuses.out_for_delivery]: {
    delivery_address_modified: normalizedTrackingStatus.delivery_address_modified,
    shipment_in_transit_to_store: normalizedTrackingStatus.shipment_in_transit_to_store,
    delivery_date_modified: normalizedTrackingStatus.delivery_date_modified,
    delivery_time_scheduled: normalizedTrackingStatus.delivery_time_scheduled,
    shipment_out_for_delivery: normalizedTrackingStatus.shipment_out_for_delivery,
    ready_for_pickup_at_collection_point: normalizedTrackingStatus.ready_for_pickup_at_collection_point,
    ready_for_pickup_unable_to_contact: normalizedTrackingStatus.ready_for_pickup_unable_to_contact,
    ready_for_pickup_waiting_for_recipient: normalizedTrackingStatus.ready_for_pickup_waiting_for_recipient,
  },
  [shipmentStatuses.delivered]: {
    delivered_to_mailbox: normalizedTrackingStatus.delivered_to_mailbox,
    delivered_to_retailer: normalizedTrackingStatus.delivered_to_retailer,
    delivered_collected_from_pup: normalizedTrackingStatus.delivered_collected_from_pup,
    delivered: normalizedTrackingStatus.delivered,
    order_refunded: normalizedTrackingStatus.order_refunded,
  },
  [shipmentStatuses.cancelled]: {
    cancel_request: normalizedTrackingStatus.cancel_request,
    order_cancelled: normalizedTrackingStatus.order_cancelled,
    label_deleted: normalizedTrackingStatus.label_deleted,
  },
}
